import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import { Stencil } from "./types";
import { ColourConverter } from "../util/colour_converter";

interface StencilListDialogProps {
    open: boolean;
    onClose: () => void;
    stencils: Stencil[];
    selectedStencil: Stencil|null;
    onSelect: (stencil: Stencil) => void;
}

export const StencilListDialog: React.FC<StencilListDialogProps> = ({
    open,
    onClose,
    stencils,
    selectedStencil,
    onSelect,
}) => {
    const generatePreview = (stencil: Stencil) => {
        const canvas = document.createElement("canvas");
        canvas.width = stencil.width;
        canvas.height = stencil.height;
        const ctx = canvas.getContext("2d")!;
        const imageData = ctx.createImageData(stencil.width, stencil.height);

        // Fill with transparent background
        for (let i = 0; i < imageData.data.length; i += 4) {
            imageData.data[i + 3] = 0; // Alpha channel
        }

        // Draw pixels
        stencil.pixels.forEach(pixel => {
            const index = (pixel.y * stencil.width + pixel.x) * 4;
            const colourConverter = new ColourConverter([]);
            const colour = colourConverter.hexToRgb(pixel.colour);
            if (colour) {
                imageData.data[index] = colour.r;
                imageData.data[index + 1] = colour.g;
                imageData.data[index + 2] = colour.b;
                imageData.data[index + 3] = 255;
            }
        });

        ctx.putImageData(imageData, 0, 0);
        return canvas.toDataURL();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                style: {
                    backgroundColor: "#2A2A2A",
                    color: "white"
                }
            }}
        >
            <DialogTitle>Select Stencil</DialogTitle>
            <DialogContent>
                <List>
                    {stencils.map((stencil) => (
                        <ListItem
                            key={stencil.id}
                            button
                            selected={selectedStencil?.id === stencil.id}
                            onClick={() => {
                                // Transform stencil data to match DrawHandler's expected format
                                const transformedStencil = {
                                    ...stencil,
                                    pixels: stencil.pixels.map(pixel => ({
                                        x: pixel.x,
                                        y: pixel.y,
                                        colour: pixel.colour // Convert 'colour' to 'colour'
                                    }))
                                };
                                onSelect(transformedStencil);
                                onClose();
                            }}
                            style={{
                                backgroundColor: selectedStencil?.id === stencil.id ? "#4A4A4A" : "transparent",
                                marginBottom: 8,
                                borderRadius: 4
                            }}
                        >
                            <ListItemAvatar>
                                <img
                                    src={generatePreview(stencil)}
                                    alt={stencil.name}
                                    style={{
                                        width: 50,
                                        height: 50,
                                        imageRendering: "pixelated",
                                        border: "1px solid #666",
                                        borderRadius: 4,
                                        backgroundColor: "#1A1A1A"
                                    }}
                                />
                            </ListItemAvatar>
                            <ListItemText
                                primary={stencil.name}
                                secondary={`${stencil.width}x${stencil.height} pixels`}
                                secondaryTypographyProps={{style: {color: "#999"}}}
                            />
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
        </Dialog>
    );
};
